import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Check, Pencil, Trash2, CalendarClock, TrendingDown, TrendingUp } from "lucide-react";
import { BolsoLayout } from "@/components/BolsoLayout";

type BillType = "payable" | "receivable";
type FilterType = "all" | "payable" | "receivable";
type FilterStatus = "pending" | "paid" | "all";

interface BillForm {
  title: string;
  amount: string;
  dueDate: string;
  type: BillType;
  recurrence: "none" | "weekly" | "monthly" | "yearly";
  notes: string;
}

const defaultForm: BillForm = {
  title: "",
  amount: "",
  dueDate: new Date().toISOString().split("T")[0],
  type: "payable",
  recurrence: "none",
  notes: "",
};

interface DueDateInfo {
  label: string;
  variant: "destructive" | "secondary" | "default" | "outline";
}

function getDueDateInfo(
  dueDate: Date,
  t: ReturnType<typeof useI18n>["t"],
  formatDate: ReturnType<typeof useI18n>["formatDate"]
): DueDateInfo {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const due = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());

  if (due < today) return { label: t.bills.overdue, variant: "destructive" };
  if (due.getTime() === today.getTime()) return { label: t.bills.dueToday, variant: "secondary" };
  if (due.getTime() === tomorrow.getTime()) return { label: t.bills.dueTomorrow, variant: "outline" };
  return { label: formatDate(due, { day: "2-digit", month: "2-digit", year: "numeric" }), variant: "default" };
}

function getRecurrenceLabel(recurrence: string, t: ReturnType<typeof useI18n>["t"]): string {
  if (recurrence === "weekly") return t.bills.recurrenceWeekly;
  if (recurrence === "monthly") return t.bills.recurrenceMonthly;
  if (recurrence === "yearly") return t.bills.recurrenceYearly;
  return t.bills.recurrenceNone;
}

export default function Bills() {
  const { t, ui, formatCurrency, formatDate } = useI18n();
  const utils = trpc.useUtils();

  const [filterType, setFilterType] = useState<FilterType>("all");
  // Mostra todas por padrão para que uma conta marcada como paga não pareça
  // ter desaparecido. O usuário ainda pode filtrar por pendentes ou pagas.
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<BillForm>(defaultForm);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: bills, isLoading } = trpc.bills.getAll.useQuery({
    type: filterType === "all" ? undefined : filterType,
    isPaid: filterStatus === "all" ? undefined : filterStatus === "paid",
  });

  const createMutation = trpc.bills.create.useMutation({
    onSuccess: () => {
      utils.bills.getAll.invalidate();
      utils.bills.getDueSoon.invalidate();
      setDialogOpen(false);
      setForm(defaultForm);
      toast.success(t.common.success);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.bills.update.useMutation({
    onSuccess: () => {
      utils.bills.getAll.invalidate();
      utils.bills.getDueSoon.invalidate();
      setDialogOpen(false);
      setEditingId(null);
      setForm(defaultForm);
      toast.success(t.common.success);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.bills.delete.useMutation({
    onSuccess: () => {
      utils.bills.getAll.invalidate();
      utils.bills.getDueSoon.invalidate();
      setDeleteId(null);
      toast.success(t.common.success);
    },
    onError: (e) => toast.error(e.message),
  });

  const markPaidMutation = trpc.bills.markPaid.useMutation({
    onSuccess: () => {
      utils.bills.getAll.invalidate();
      utils.bills.getDueSoon.invalidate();
      toast.success(t.common.success);
    },
    onError: (e) => toast.error(e.message),
  });

  function openCreate() {
    setEditingId(null);
    setForm(defaultForm);
    setDialogOpen(true);
  }

  function openEdit(bill: NonNullable<typeof bills>[number]) {
    setEditingId(bill.id);
    setForm({
      title: bill.title,
      amount: String(bill.amount),
      dueDate: new Date(bill.dueDate).toISOString().split("T")[0],
      type: bill.type,
      recurrence: bill.recurrence,
      notes: bill.notes ?? "",
    });
    setDialogOpen(true);
  }

  function handleSubmit() {
    if (!form.title.trim() || !form.amount || !form.dueDate) {
      toast.error(ui.bills.required);
      return;
    }
    const dueDate = new Date(form.dueDate + "T12:00:00");
    const payload = {
      title: form.title.trim(),
      amount: parseFloat(form.amount).toFixed(2),
      dueDate,
      type: form.type,
      recurrence: form.recurrence,
      notes: form.notes || null,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const totalPayable = bills?.filter((b) => b.type === "payable" && !b.isPaid).reduce((s, b) => s + parseFloat(String(b.amount)), 0) ?? 0;
  const totalReceivable = bills?.filter((b) => b.type === "receivable" && !b.isPaid).reduce((s, b) => s + parseFloat(String(b.amount)), 0) ?? 0;

  return (
    <BolsoLayout onNewTransaction={openCreate}>
      <div className="container py-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t.bills.title}</h1>
          <p className="text-muted-foreground text-sm mt-1">{ui.bills.subtitle}</p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-2">
          <Plus className="w-4 h-4" />
          {t.bills.new}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="border-red-200 dark:border-red-900">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-1">
              <TrendingDown className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">{t.bills.payable}</span>
            </div>
            <p className="text-xl font-bold text-red-600 dark:text-red-400">{formatCurrency(totalPayable)}</p>
            <p className="text-xs text-muted-foreground">{ui.bills.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 dark:border-green-900">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wide">{t.bills.receivable}</span>
            </div>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{formatCurrency(totalReceivable)}</p>
            <p className="text-xs text-muted-foreground">{ui.bills.receivable}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Tabs aria-label={t.bills.type} value={filterType} onValueChange={(v) => setFilterType(v as FilterType)}>
          <TabsList>
            <TabsTrigger value="all">{t.bills.all}</TabsTrigger>
            <TabsTrigger value="payable">{t.bills.payable}</TabsTrigger>
            <TabsTrigger value="receivable">{t.bills.receivable}</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs aria-label={t.bills.paid} value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
          <TabsList>
            <TabsTrigger value="pending">{t.bills.pending}</TabsTrigger>
            <TabsTrigger value="paid">{t.bills.paid}</TabsTrigger>
            <TabsTrigger value="all">{t.bills.all}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      ) : !bills || bills.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <CalendarClock className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="font-medium text-muted-foreground">{t.bills.noBills}</p>
            <p className="text-sm text-muted-foreground mt-1">{t.bills.addFirst}</p>
            <Button onClick={openCreate} className="mt-4 gap-2">
              <Plus className="w-4 h-4" />
              {t.bills.new}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {bills.map((bill) => {
            const { label: dueDateLabel, variant: dueDateVariant } = getDueDateInfo(new Date(bill.dueDate), t, formatDate);
            return (
              <Card key={bill.id} className={bill.isPaid ? "opacity-60" : ""}>
                <CardContent className="flex items-center gap-3 py-3 px-4">
                  {/* Mark paid button */}
                  <button
                    onClick={() => markPaidMutation.mutate({ id: bill.id, paid: !bill.isPaid })}
                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      bill.isPaid
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-muted-foreground hover:border-green-500"
                    }`}
                    title={bill.isPaid ? t.bills.markUnpaid : t.bills.markPaid}
                    aria-label={bill.isPaid ? t.bills.markUnpaid : t.bills.markPaid}
                  >
                    {bill.isPaid && <Check className="w-4 h-4" />}
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-sm ${bill.isPaid ? "line-through" : ""}`}>{bill.title}</span>
                      <Badge variant={bill.type === "payable" ? "destructive" : "default"} className="text-xs">
                        {bill.type === "payable" ? t.bills.payable : t.bills.receivable}
                      </Badge>
                      {!bill.isPaid && (
                        <Badge variant={dueDateVariant} className="text-xs">
                          {dueDateLabel}
                        </Badge>
                      )}
                      {bill.recurrence !== "none" && (
                        <Badge variant="outline" className="text-xs">
                          {getRecurrenceLabel(bill.recurrence, t)}
                        </Badge>
                      )}
                    </div>
                    {bill.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate">{bill.notes}</p>}
                  </div>

                  {/* Amount */}
                  <div className="text-right flex-shrink-0">
                    <p className={`font-semibold text-sm ${bill.type === "payable" ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                      {formatCurrency(parseFloat(String(bill.amount)))}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(bill)} aria-label={t.bills.edit}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteId(bill.id)} aria-label={t.bills.delete}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingId(null);
            setForm(defaultForm);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? t.bills.edit : t.bills.new}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t.bills.titleLabel} *</Label>
              <Input
                placeholder={t.bills.titlePlaceholder}
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.bills.amount} *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0,00"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t.bills.dueDate} *</Label>
                <Input
                  type="date"
                  value={form.dueDate}
                  onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t.bills.type}</Label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v as BillType }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payable">{t.bills.payable}</SelectItem>
                    <SelectItem value="receivable">{t.bills.receivable}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t.bills.recurrence}</Label>
                <Select
                  value={form.recurrence}
                  onValueChange={(v) => setForm((f) => ({ ...f, recurrence: v as BillForm["recurrence"] }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.bills.recurrenceNone}</SelectItem>
                    <SelectItem value="weekly">{t.bills.recurrenceWeekly}</SelectItem>
                    <SelectItem value="monthly">{t.bills.recurrenceMonthly}</SelectItem>
                    <SelectItem value="yearly">{t.bills.recurrenceYearly}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t.bills.notes}</Label>
              <Input
                placeholder={ui.bills.notesPlaceholder}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t.bills.cancel}
            </Button>
            <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
              {t.bills.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={deleteId !== null} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.bills.confirmDelete}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t.bills.confirmDeleteDesc}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              {t.bills.cancel}
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteId !== null && deleteMutation.mutate({ id: deleteId })}
              disabled={deleteMutation.isPending}
            >
              {t.bills.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </BolsoLayout>
  );
}
